import React, { useMemo, useState } from 'react';
import {
  Button, Card, Col, Input, Popconfirm, Row, Select, Space, Tabs, Upload, message
} from 'antd';
import { UploadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  IconThumb,
  useBuiltInIcons,
  useUploadedIcons,
  listSpeedBands,
  listIconStyles
} from '../../../modules/iconLibrary';

/** 管理后台 · 地图配置 · 图标库管理 */
export function buildIconLibraryTab(context) {
  const { shouldShowPage } = context;
  if (!shouldShowPage('图标库管理') && !shouldShowPage('地图设置') && !shouldShowPage('系统设置')) {
    return null;
  }

  return {
    key: 'icon_library',
    label: '图标库管理',
    children: <IconLibraryAdminPanel />
  };
}

function BuiltinGrid({ icons }) {
  return (
    <Row gutter={[12, 12]}>
      {icons.map((ic) => (
        <Col key={ic.value} xs={12} sm={8} md={6} lg={4}>
          <Card size="small" styles={{ body: { textAlign: 'center', padding: 12 } }}>
            <IconThumb iconKey={ic.value} url={ic.url} size={44} />
            <div style={{ fontWeight: 600, marginTop: 8 }}>{ic.label}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{ic.key}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{ic.description}</div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function IconLibraryAdminPanel() {
  const builtIn = useBuiltInIcons();
  const uploaded = useUploadedIcons();
  const [editName, setEditName] = useState({});
  const [styleId, setStyleId] = useState('flat');
  const speedBands = useMemo(() => listSpeedBands(), []);
  const styles = useMemo(() => listIconStyles(), []);
  const currentStyle = styles.find((s) => s.id === styleId) || styles[0];
  const filteredBuiltin = useMemo(
    () => (builtIn.icons || []).filter((ic) => ic.style === styleId),
    [builtIn.icons, styleId]
  );

  const handleUpload = async (file) => {
    try {
      await uploaded.uploadIcon(file, { description: '自定义图标' });
      message.success('已上传到图标库');
    } catch (e) {
      message.error(e.message || '上传失败');
    }
    return false;
  };

  const saveMeta = async (ic) => {
    const name = (editName[ic.id] ?? ic.name ?? '').trim();
    if (!name) {
      message.warning('名称不能为空');
      return;
    }
    try {
      await uploaded.updateIcon(ic.id, { name, description: ic.description || '' });
      message.success('已保存');
    } catch (e) {
      message.error(e.message || '保存失败');
    }
  };

  return (
    <div>
      <h3>图标库管理</h3>
      <p style={{ color: '#666', marginBottom: 16 }}>
        内置图标在下方用风格下拉切换。指挥台队员坐标默认灰色水滴；浅蓝/橙/红/深蓝/灰表示在线、暂时离线、选中、高亮、离线。彩色水滴为旧版整针着色。
        上传库可供全景挂点、自定义标记等选用。
      </p>

      <Tabs
        items={[
          {
            key: 'builtin',
            label: `内置图标（${filteredBuiltin.length}）`,
            children: (
              <div>
                <Space wrap style={{ marginBottom: 12 }} align="center">
                  <span>风格</span>
                  <Select
                    value={styleId}
                    style={{ minWidth: 160 }}
                    onChange={setStyleId}
                    options={styles.map((s) => ({ value: s.id, label: s.label }))}
                  />
                  <span style={{ color: '#888', fontSize: 13 }}>{currentStyle?.description}</span>
                </Space>
                {styleId === 'flat' && (
                  <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
                    速度档位：
                    {speedBands.map((b) => (
                      <span key={b.key} style={{ marginRight: 10 }}>
                        {b.label}
                        {b.maxMs != null ? ` <${b.maxMs}m/s` : '及以上'}
                      </span>
                    ))}
                  </div>
                )}
                <BuiltinGrid icons={filteredBuiltin} />
              </div>
            )
          },
          {
            key: 'uploaded',
            label: `上传库（${uploaded.icons.length}）`,
            children: (
              <div>
                <Space wrap style={{ marginBottom: 16 }}>
                  <Upload
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
                    showUploadList={false}
                    beforeUpload={handleUpload}
                  >
                    <Button type="primary" icon={<UploadOutlined />} loading={uploaded.loading}>
                      上传图标
                    </Button>
                  </Upload>
                  <Button icon={<ReloadOutlined />} onClick={() => uploaded.reload()}>
                    刷新
                  </Button>
                </Space>
                {uploaded.error && (
                  <div style={{ color: '#cf1322', marginBottom: 12 }}>{uploaded.error}</div>
                )}
                <Row gutter={[12, 12]}>
                  {uploaded.icons.map((ic) => (
                    <Col key={ic.id} xs={12} sm={8} md={6} lg={4}>
                      <Card
                        size="small"
                        styles={{ body: { padding: 12 } }}
                        actions={[
                          <Button
                            key="save"
                            type="link"
                            size="small"
                            onClick={() => saveMeta(ic)}
                          >
                            保存名称
                          </Button>,
                          <Popconfirm
                            key="del"
                            title="删除该图标？"
                            onConfirm={async () => {
                              try {
                                await uploaded.deleteIcon(ic.id);
                                message.success('已删除');
                              } catch (e) {
                                message.error(e.message || '删除失败');
                              }
                            }}
                          >
                            <Button type="link" danger size="small">删除</Button>
                          </Popconfirm>
                        ]}
                      >
                        <div style={{ textAlign: 'center', marginBottom: 8 }}>
                          <IconThumb
                            iconId={ic.id}
                            url={ic.url || ic.file_path}
                            size={48}
                            alt={ic.name || ''}
                          />
                        </div>
                        <Input
                          size="small"
                          value={editName[ic.id] ?? ic.name ?? ''}
                          onChange={(e) => setEditName((prev) => ({
                            ...prev,
                            [ic.id]: e.target.value
                          }))}
                          placeholder="名称"
                        />
                        <div style={{ fontSize: 11, color: '#999', marginTop: 6, wordBreak: 'break-all' }}>
                          #{ic.id} {ic.file_path}
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {!uploaded.loading && uploaded.icons.length === 0 && (
                  <div style={{ color: '#888' }}>暂无上传图标，可点击上方上传。</div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
}
